import React, {PureComponent} from 'react';
import Resize from 'react-resize-detector';
import PropTypes from 'prop-types';
import _ from 'lodash';

import HoverOver from '../HoverOver';
import UsaOutline from "../MapElements/UsaOutline";
import UsaStateHovers from "../MapElements/UsaStateHovers";
import UsaStateBorders from "../MapElements/UsaStateBorders";
import UsaStateShapes from "../MapElements/UsaStateShapes";
import UsaStateLabels from "../MapElements/UsaStateLabels";
import UsaStateCalloutBoxes from "../MapElements/UsaStateCalloutBoxes";
import PatternCrosshatch from "../MapElements/PatternCrosshatch";

import {TEXT} from "../../../utilities/strings";

import '../MapElements/Map.css';

/**
 * Component for rendering the national map at the state level with
 * all borders, labels, and colored state shapes and callouts.
 *
 * @property colorField - field in data objects that holds the quartile value for classification
 * @property legendField - field in data objects that holds the numeric value for classification
 * @property data - array of location models to calculate the map colors and values
 * @property dict - dictionary of data keyed by the state fips
 * @property selectedCost - selected cost string value
 * @property selectedIds - array of county fips ids to style as selected
 * @property onClick - callback when a state is clicked (stateFips,cent)
 * @property onAltClick - callback when a state is alt clicked (stateFips,cent)
 *
 * @example
 * return (
 *   <NationalMap
 *     colorField={mapProperty}
 *     legendField={legendProperty}
 *     data={this.props.statesData}
 *     dict={_.keyBy(this.props.statesData, VARIABLES.StateID)}
 *     selectedCost={this.props.selectedCost}
 *     selectedIds={[this.props.selectedStateFips, this.props.comparisonStateFips]}
 *     onClick={this.props.onSelectedStateChange}
 *     onAltClick={this.props.onComparisonStateChange}
 *   />
 * )
 */
export default class NationalMap extends PureComponent {

  static propTypes = {
    colorField: PropTypes.string,
    legendField: PropTypes.string,
    selectedCost: PropTypes.string,
    selectedIds: PropTypes.array,
    data: PropTypes.array,
    dict: PropTypes.object,
    onClick: PropTypes.func,
    onAltClick: PropTypes.func,
  }

  wrapper = React.createRef()

  state = {
    width: 500,
    height: 250
  }

  /**
   * Size map initially
   */
  componentDidMount() {
    this.handleResize(this.wrapper.current.offsetWidth)
  }

  /**
   * Check if window has resized, and size map accordingly
   */
  handleResize = (resizedWidth) => {
    const width = Math.max(360, resizedWidth)
    const height = Math.max(240, width * .57)

    this.setState({width, height});
  }

  /**
   * get data for mouse hover over for currently hovered location
   * and set hover state value for display
   */
  handleHoverOver = (stateFips, coords) => {
    const hover = {
      data: _.get(this.props, ['dict', stateFips], null),
      props: this.props,
      coords,
    };
    this.setState({hover});
  }

  /**
   * clear hover display
   */
  handleHoverOut = () => {
    this.setState({hover: null});
  }

  render() {
    return (
      <div ref={this.wrapper} className="NationalMap SvgMap">
        <Resize handleWidth onResize={this.handleResize.bind(this)}/>
        <svg id="national-map" role="img"
             aria-labelledby="national-map-desc"
             width={this.state.width}
             height={this.state.height}>
          <desc id="national-map-desc">{TEXT.accessible_map_description}</desc>
          <PatternCrosshatch/>
          <UsaOutline
            width={this.state.width}
            height={this.state.height}
          />
          <UsaStateShapes
            data={this.props.data}
            dict={this.props.dict}
            width={this.state.width}
            height={this.state.height}
            dimension={this.props.colorField}
          />
          <UsaStateBorders
            width={this.state.width}
            height={this.state.height}
          />
          <UsaStateLabels
            width={this.state.width}
            height={this.state.height}
            data={this.props.data}
            dict={this.props.dict}
            dimension={this.props.colorField}
          />
          <UsaStateHovers
            width={this.state.width}
            height={this.state.height}
            selectedIds={this.props.selectedIds}
            onClick={this.props.onClick}
            onAltClick={this.props.onAltClick}
            onHoverOver={this.handleHoverOver}
            onHoverOut={this.handleHoverOut}
          />
          <UsaStateCalloutBoxes
            width={this.state.width}
            height={this.state.height}
            data={this.props.data}
            dict={this.props.dict}
            dimension={this.props.colorField}
            onClick={this.props.onClick}
            onAltClick={this.props.onAltClick}
            onHoverOver={this.handleHoverOver}
            onHoverOut={this.handleHoverOut}
          />
        </svg>
        <HoverOver data={this.state.hover}/>
      </div>
    );
  }
}
